import Joi from "joi";

// Create course validation
export const createCourseValidation = (req: any, res: any, next: any) => {
  // Parse learningOutcomes if sent as JSON string (multipart/form-data)
  const body = { ...req.body };
  if (
    body.learningOutcomes !== undefined &&
    typeof body.learningOutcomes === "string"
  ) {
    try {
      const parsed = JSON.parse(body.learningOutcomes);
      body.learningOutcomes = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      body.learningOutcomes = [body.learningOutcomes];
    }
  }

  const schema = Joi.object({
    semesterId: Joi.string()
      .pattern(/^[0-9a-fA-F]{24}$/)
      .required()
      .messages({
        "string.pattern.base": "Semester ID must be a valid MongoDB ObjectId",
        "any.required":
          "Semester ID is required. Courses must be created within a semester context.",
      }),
    courseCode: Joi.string().trim().min(2).max(20).required().messages({
      "string.min": "Course code must be at least 2 characters",
      "string.max": "Course code cannot exceed 20 characters",
      "any.required": "Course code is required",
    }),
    courseName: Joi.string().trim().min(2).max(200).required().messages({
      "string.min": "Course name must be at least 2 characters",
      "string.max": "Course name cannot exceed 200 characters",
      "any.required": "Course name is required",
    }),
    description: Joi.string().trim().max(1000).optional().messages({
      "string.max": "Description cannot exceed 1000 characters",
    }),
    credits: Joi.number().min(0).max(20).optional().messages({
      "number.min": "Credits must be at least 0",
      "number.max": "Credits cannot exceed 20",
    }),
    prerequisites: Joi.array()
      .items(
        Joi.string()
          .pattern(/^[0-9a-fA-F]{24}$/)
          .messages({
            "string.pattern.base": "Invalid prerequisite course ID format",
          }),
      )
      .optional(),
    department: Joi.string().trim().max(100).optional().messages({
      "string.max": "Department cannot exceed 100 characters",
    }),
    level: Joi.string()
      .trim()
      .valid("undergraduate", "graduate", "doctoral", "certificate")
      .optional()
      .messages({
        "any.only":
          "Level must be one of: undergraduate, graduate, doctoral, certificate",
      }),
    courseType: Joi.string().trim().max(50).optional().messages({
      "string.max": "Course type cannot exceed 50 characters",
    }),
    maxStudents: Joi.number().min(1).max(10_000).optional().messages({
      "number.min": "Max students must be at least 1",
      "number.max": "Max students cannot exceed 10000",
    }),
    learningOutcomes: Joi.array()
      .items(Joi.string().trim().max(500))
      .optional()
      .messages({
        "array.base": "Learning outcomes must be an array of strings",
      }),
    isActive: Joi.boolean().optional(),
  });

  const { error, value } = schema.validate(body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};

// Update course validation
export const updateCourseValidation = (req: any, res: any, next: any) => {
  const schema = Joi.object({
    courseCode: Joi.string().trim().min(2).max(20).optional().messages({
      "string.min": "Course code must be at least 2 characters",
      "string.max": "Course code cannot exceed 20 characters",
    }),
    courseName: Joi.string().trim().min(2).max(200).optional().messages({
      "string.min": "Course name must be at least 2 characters",
      "string.max": "Course name cannot exceed 200 characters",
    }),
    description: Joi.string().trim().max(1000).optional().messages({
      "string.max": "Description cannot exceed 1000 characters",
    }),
    credits: Joi.number().min(0).max(20).optional().messages({
      "number.min": "Credits must be at least 0",
      "number.max": "Credits cannot exceed 20",
    }),
    prerequisites: Joi.array()
      .items(
        Joi.string()
          .pattern(/^[0-9a-fA-F]{24}$/)
          .messages({
            "string.pattern.base": "Invalid prerequisite course ID format",
          }),
      )
      .allow(null)
      .optional(),
    department: Joi.string().trim().max(100).optional().messages({
      "string.max": "Department cannot exceed 100 characters",
    }),
    level: Joi.string()
      .trim()
      .valid("undergraduate", "graduate", "doctoral", "certificate")
      .optional()
      .messages({
        "any.only":
          "Level must be one of: undergraduate, graduate, doctoral, certificate",
      }),
    status: Joi.string()
      .valid("active", "inactive", "archived")
      .optional()
      .messages({
        "any.only": "Status must be one of: active, inactive, archived",
      }),
    isActive: Joi.boolean().optional(),
    courseType: Joi.string().trim().max(50).optional().messages({
      "string.max": "Course type cannot exceed 50 characters",
    }),
    maxStudents: Joi.number().min(1).max(10_000).optional().messages({
      "number.min": "Max students must be at least 1",
      "number.max": "Max students cannot exceed 10000",
    }),
    learningOutcomes: Joi.array()
      .items(Joi.string().trim().max(500))
      .optional()
      .messages({
        "array.base": "Learning outcomes must be an array of strings",
      }),
  });

  const { error, value } = schema.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};

// Course status validation
export const courseStatusValidation = (req: any, res: any, next: any) => {
  const schema = Joi.object({
    status: Joi.string()
      .valid("active", "inactive", "archived")
      .required()
      .messages({
        "any.only": "Status must be one of: active, inactive, archived",
        "any.required": "Status is required",
      }),
  });

  const { error, value } = schema.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};
